"use client";

import React from "react";
import { BookOpen, Users, Compass, MoonStar, Scroll, Globe as Globe2, Landmark, ArrowRight, ShieldCheck } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
interface DisciplineItem {
  id: number;
  title: string;
  order: string;
  description: string;
  scope: string;
}
const disciplines: DisciplineItem[] = [{
  id: 1,
  title: "Triết học Dịch lý",
  order: "01",
  description: "Nghiên cứu quy luật biến dịch âm dương, ngũ hành và hệ thống bát quái làm cơ sở lý luận cho sự tương tác giữa con người và môi trường sinh thái.",
  scope: "Nguyên lý vũ trụ quan & sinh thái tự nhiên"
}, {
  id: 2,
  title: "Khoa học Nhân sinh",
  order: "02",
  description: "Khảo sát mối quan hệ tổng hòa giữa trường khí, môi sinh cư trú với tâm sinh lý, sức khỏe và hành vi phát triển của cộng đồng cư dân.",
  scope: "Nhân chủng học, sinh lý học môi trường & xã hội"
}, {
  id: 3,
  title: "Địa lý Phong thủy",
  order: "03",
  description: "Đánh giá cấu trúc địa hình, thế đất, dòng chảy và trường khí tự nhiên nhằm xác lập quy chuẩn quy hoạch không gian kiến trúc hài hòa bền vững.",
  scope: "Quy hoạch cảnh quan, mạch khí & định hướng công trình"
}, {
  id: 4,
  title: "Thiên văn Lịch pháp",
  order: "04",
  description: "Ứng dụng chuyển động thiên thể, chu kỳ thời tiết và quy luật thời gian trong tính toán thời khắc, tiết khí và nhịp điệu sinh học của đất trời.",
  scope: "Hệ thống tiết khí, tọa độ không gian & lịch số học"
}, {
  id: 5,
  title: "Hán Nôm cổ học",
  order: "05",
  description: "Dịch thuật, khảo cứu văn bia, địa bạ và thư tịch cổ nhằm giải mã tri thức địa lý học truyền thống và kinh nghiệm định cư ngàn đời của tiền nhân.",
  scope: "Văn bản học, tư liệu di sản & ngữ văn Hán Nôm"
}, {
  id: 6,
  title: "Khoa học Trái đất",
  order: "06",
  description: "Tích hợp kiến thức địa chất công trình, địa mạo học, thổ nhưỡng học và từ trường trái đất dưới lăng kính khoa học hiện đại thực chứng.",
  scope: "Địa từ trường, vi khí hậu & cấu trúc tầng đất đá"
}, {
  id: 7,
  title: "Tôn giáo Tín ngưỡng",
  order: "07",
  description: "Tìm hiểu chiều kích tinh thần, biểu tượng văn hóa tâm linh và tín ngưỡng thờ phụng gắn liền với không gian thiêng trong cộng đồng người Việt.",
  scope: "Văn hóa tâm linh, tín ngưỡng bản địa & không gian thiêng"
}];
const icons = [BookOpen, Users, Compass, MoonStar, Scroll, Globe2, Landmark];
export default function Geomancy() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.08
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 16
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  return <section id="khoa-học-địa-lý-phong-thủy" data-nav="light" className="relative bg-background py-16 md:py-24 border-b border-border">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        {/* Section Heading with Institutional Dual-Bar Motif */}
        <div className="max-w-3xl mb-12 md:mb-16">
          <div className="inline-flex items-center gap-2 px-3 py-1 mb-4 rounded-sm bg-accent text-accent-foreground border border-border text-xs font-semibold tracking-wide uppercase">
            <ShieldCheck className="size-3.5 text-primary" />
            Lĩnh vực học thuật trọng tâm
          </div>

          <h2 className="text-2xl md:text-3xl lg:text-4xl font-semibold tracking-tight text-secondary text-balance">
            Khoa học Địa lý Phong thủy
          </h2>

          <div className="mt-4 mb-5 flex items-center gap-2">
            <div className="h-1 w-12 bg-secondary rounded-xs" />
            <div className="h-px w-24 bg-border" />
          </div>

          <p className="text-base md:text-lg text-muted-foreground leading-relaxed">
            Hệ thống hóa tri thức truyền thống kết hợp phương pháp nghiên cứu liên ngành hiện đại. Bảy chuyên ngành học thuật tạo nền móng vững chắc cho công tác bảo tồn, ứng dụng và định hướng quy hoạch môi trường sống hài hòa.
          </p>
        </div>

        {/* 7 Academic Disciplines Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true,
        amount: 0.15
      }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {disciplines.map((item, index) => {
          const IconComponent = icons[index % icons.length];
          return <motion.article key={item.id} data-index={index} variants={itemVariants} className="group relative flex flex-col justify-between p-6 md:p-7 bg-card text-card-foreground rounded-sm border border-border border-t-3 border-t-primary shadow-xs hover:shadow-md transition-[border-color,box-shadow] duration-200">
                <div>
                  {/* Top Bar: Icon + Order Index */}
                  <div className="flex items-center justify-between gap-4 mb-5">
                    <div className="size-11 rounded-sm bg-accent flex items-center justify-center text-primary group-hover:bg-primary group-hover:text-primary-foreground transition-colors duration-200">
                      <IconComponent className="size-5" />
                    </div>
                    <span className="text-xs font-mono font-medium text-muted-foreground px-2 py-0.5 rounded-xs bg-muted border border-border">
                      {item.order}
                    </span>
                  </div>

                  {/* Title */}
                  <h3 className="text-lg md:text-xl font-semibold text-foreground tracking-tight mb-3">
                    {item.title}
                  </h3>

                  {/* Academic Description */}
                  <p className="text-sm md:text-base text-muted-foreground leading-relaxed mb-6">
                    {item.description}
                  </p>
                </div>

                {/* Scope Metadata */}
                <div className="pt-4 border-t border-border flex items-center justify-between text-xs text-muted-foreground">
                  <span className="font-medium truncate pr-2">
                    {item.scope}
                  </span>
                </div>
              </motion.article>;
        })}
        </motion.div>

        {/* Bottom Institutional Note and Context Action */}
        <div className="mt-12 md:mt-16 p-6 rounded-sm bg-muted border border-border flex flex-col sm:flex-row sm:items-center justify-between gap-6">
          <div className="max-w-2xl">
            <h4 className="text-sm md:text-base font-semibold text-foreground mb-1">
              Tiếp cận khoa học thực chứng và tôn trọng di sản
            </h4>
            <p className="text-xs md:text-sm text-muted-foreground leading-relaxed">
              Mọi hoạt động nghiên cứu đều được thẩm định thông qua Hội đồng Khoa học của Hội Địa lý Môi trường Việt Nam nhằm loại bỏ các yếu tố suy diễn mê tín, giữ gìn bản sắc văn hóa dân tộc.
            </p>
          </div>
          <div className="shrink-0">
            <Button asChild variant="outline" className="border-primary text-primary hover:bg-accent font-semibold text-xs md:text-sm rounded-sm">
              <Link to="/ContactPage">
                <span>Liên hệ hợp tác nghiên cứu</span>
                <ArrowRight className="size-4 ml-1" />
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}